"use client";

import { useLocale, useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Filter } from "lucide-react";
import {
  Form,
  FormControl,
  FormField,
  FormDescription,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { debounce, formatPrice } from "@/lib/utils";

const MAX_PRICE = 500_000;

export default function ProductFilters({ categories }) {
  const t = useTranslations("shopProductFilters");
  const locale = useLocale();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setSelectedCategories(
      searchParams.get("categories")?.split(",").map(Number) || [],
    );
  }, [searchParams]);

  const updateURL = (newParams) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(newParams).forEach(([key, value]) => {
      if (
        value === null ||
        value === false ||
        value === "" ||
        (Array.isArray(value) && value.length === 0)
      ) {
        params.delete(key);
      } else {
        params.set(
          key,
          Array.isArray(value) ? value.join(",") : value.toString(),
        );
      }
    });
    router.push(`?${params.toString()}`);
  };

  const handleCategoryChange = (categoryId) => {
    const newSelectedCategories = selectedCategories.includes(categoryId)
      ? selectedCategories.filter((id) => id !== categoryId)
      : [...selectedCategories, categoryId];
    setSelectedCategories(newSelectedCategories);

    updateURL({
      categories: newSelectedCategories,
    });
  };

  const formatRange = (price) =>
    price === MAX_PRICE ? "∞" : formatPrice(price, locale, false);

  const FiltersContent = () => {
    const form = useForm({
      defaultValues: {
        search: searchParams.get("search") || "",
        sort: searchParams.get("sort") || "new",
        inStockOnly: searchParams.get("inStockOnly") === "true" || false,
        inPromoOnly: searchParams.get("inPromoOnly") === "true" || false,
        priceRange: [
          parseInt(searchParams.get("minPrice") || "0"),
          parseInt(searchParams.get("maxPrice") || MAX_PRICE.toString()),
        ],
        categories:
          searchParams.get("categories")?.split(",").map(Number) || [],
      },
    });

    const debouncedUpdateURL = debounce((params) => {
      updateURL(params);
    }, 1000);

    const handleFilterChange = (field, value) => {
      field.onChange(value);
      debouncedUpdateURL(value);
    };

    return (
      <Form {...form}>
        <form className="flex flex-wrap items-center space-y-3">
          <FormField
            control={form.control}
            name="search"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>{t("searchByName")}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t("searchPlaceholder")}
                    defaultValue={field.value}
                    onKeyUp={(e) =>
                      handleFilterChange(field, { search: e.target.value })
                    }
                    type="search"
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="sort"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>{t("sortBy")}</FormLabel>
                <Select
                  dir={locale === "ar" ? "rtl" : "ltr"}
                  onValueChange={(e) => handleFilterChange(field, { sort: e })}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={t("selectSortOption")} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="new">{t("recent")}</SelectItem>
                    <SelectItem value="best-rating">{t("rating")}</SelectItem>
                    <SelectItem value="a-z">{t("alphaAscending")}</SelectItem>
                    <SelectItem value="z-a">{t("alphaDescending")}</SelectItem>
                    <SelectItem value="price-asc">
                      {t("priceAscending")}
                    </SelectItem>
                    <SelectItem value="price-desc">
                      {t("priceDescending")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="priceRange"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>{t("priceRange")}</FormLabel>
                <FormControl>
                  <Slider
                    isRange
                    min={0}
                    max={MAX_PRICE}
                    step={5000}
                    defaultValue={field.value}
                    onValueChange={(value) => {
                      field.onChange(value);
                      debouncedUpdateURL({
                        minPrice: value[0],
                        maxPrice: value[1],
                      });
                    }}
                  />
                </FormControl>
                <FormDescription className="flex justify-between">
                  <div className="mt-2 flex w-full justify-between">
                    <span className="text-xs">
                      {formatRange(field.value[0])}
                    </span>
                    <span className="text-xs">
                      {formatRange(field.value[1])}
                    </span>
                  </div>
                </FormDescription>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="inStockOnly"
            render={({ field }) => (
              <FormItem className="flex w-full items-center gap-2.5">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={(value) => {
                      field.onChange(value);
                      debouncedUpdateURL({ inStockOnly: value });
                    }}
                  />
                </FormControl>
                <FormLabel>{t("inStockOnly")}</FormLabel>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="inPromoOnly"
            render={({ field }) => (
              <FormItem className="flex w-full items-center gap-2.5">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={(value) => {
                      field.onChange(value);
                      debouncedUpdateURL({ inPromoOnly: value });
                    }}
                  />
                </FormControl>
                <FormLabel>{t("inPromoOnly")}</FormLabel>
              </FormItem>
            )}
          />

          <div className="pt-5">
            <p className="mb-2 text-lg font-medium">{t("categories")}</p>
            <div className="space-y-2">
              {categories.map((category) => {
                const categoryName =
                  locale === "fr"
                    ? category.category_name
                    : locale === "ar"
                      ? category.category_name_ar
                      : category.category_name_en;
                return (
                  <div
                    key={category.id_category}
                    className="flex items-center space-x-2"
                  >
                    <Checkbox
                      id={`category-${category.id_category}`}
                      checked={selectedCategories.includes(
                        category.id_category,
                      )}
                      onCheckedChange={() =>
                        handleCategoryChange(category.id_category)
                      }
                    />
                    <Label htmlFor={`category-${category.id_category}`}>
                      {categoryName}
                    </Label>
                  </div>
                );
              })}
            </div>
          </div>
        </form>
      </Form>
    );
  };

  return (
    <>
      {/* Desktop view */}
      <div className="sticky top-[168px] hidden space-y-6 rounded-lg bg-white p-6 shadow md:block">
        <FiltersContent />
      </div>

      {/* Mobile view */}
      <div className="md:hidden">
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" className="w-full">
              <Filter className="mr-2 h-4 w-4" />
              {t("filters")}
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[300px] sm:w-[400px]">
            <SheetHeader>
              <SheetTitle>{t("filters")}</SheetTitle>
              <SheetDescription>{t("adjustFilters")}</SheetDescription>
            </SheetHeader>
            <div className="mt-6 max-h-[calc(100vh-120px)] overflow-y-auto p-1">
              <FiltersContent />
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </>
  );
}

/* const oldFiltersContent = () => (
  <div className="space-y-6">
    <div>
      <Label htmlFor="search">{t("searchByName")}</Label>
      <Input
        id="search"
        placeholder={t("searchPlaceholder")}
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          updateURL({ search: e.target.value });
        }}
      />
    </div>

    <div>
      <Label htmlFor="sort">{t("sortBy")}</Label>
      <Select
        value={sort}
        onValueChange={(value) => {
          setSort(value);
          updateURL({ sort: value });
        }}
      >
        <SelectTrigger id="sort">
          <SelectValue placeholder={t("selectSortOption")} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="new">{t("recent")}</SelectItem>
          <SelectItem value="a-z">A-Z</SelectItem>
          <SelectItem value="z-a">Z-A</SelectItem>
          <SelectItem value="price-asc">{t("priceAscending")}</SelectItem>
          <SelectItem value="price-desc">{t("priceDescending")}</SelectItem>
        </SelectContent>
      </Select>
    </div>

    <div>
      <Label htmlFor="price-range">{t("priceRange")}</Label>
      <div className="mt-2.5 flex flex-col items-center">
        <Slider
          id="price-range"
          isRange
          min={0}
          max={MAX_PRICE}
          step={5000}
          value={priceRange}
          onValueChange={(value) => {
            setPriceRange(value);
            updateURL({ minPrice: value[0], maxPrice: value[1] });
          }}
          className="w-full"
        />
        <div className="mt-2 flex w-full justify-between">
          <span className="text-xs">{formatRange(priceRange[0])}</span>
          <span className="text-xs">{formatRange(priceRange[1])}</span>
        </div>
      </div>
    </div>

    <div>
      <p className="mb-2 text-lg font-medium">{t("categories")}</p>
      <div className="space-y-2">
        {categories.map((category) => {
          const categoryName =
            locale === "fr"
              ? category.category_name
              : locale === "ar"
                ? category.category_name_ar
                : category.category_name_en;
          return (
            <div
              key={category.id_category}
              className="flex items-center space-x-2"
            >
              <Checkbox
                id={`category-${category.id_category}`}
                checked={selectedCategories.includes(category.id_category)}
                onCheckedChange={() =>
                  handleCategoryChange(category.id_category)
                }
              />
              <Label htmlFor={`category-${category.id_category}`}>
                {categoryName}
              </Label>
            </div>
          );
        })}
      </div>
    </div>
  </div>
); */
